import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import { ModalConfirm } from '@components/Modal';
import Button from '@components/Button';
import PageHeader from '@features/PageHeader';
import { useTranslation } from '@hooks';
import { ChannelsIcon } from 'assets/images/icons';
import { channelsActions, accountActions } from '@actions';
import { channelsService } from '@services';
import { handleApiError } from '@helpers/api';
import { CHANNEL, STATUS } from '@constants/channels';
import ChannelItem from './ChannelItem';
import EditModal from './EditModal';
import CreateModal from './CreateModal';

const Container = styled('div')({
    paddingBottom: 24,
    '@media (max-width: 1024px)': {
        overflowY: 'auto',
    },
});

const CardsWrapper = styled('div')({
    display: 'grid',
    gridGap: 16,
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    '@media (max-width: 480px)': {
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    },
});

const Channels = () => {
    const { t } = useTranslation();

    const [createModal, setCreateModal] = useState({
        open: false,
        step: 1,
        type: CHANNEL.SMS,
        status: STATUS.PENDING,
        qrCode: '',
        liveChatPublicId: null,
    });
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState();

    const companies = useSelector(state => state.account?.account?.companies || []);
    const brands = useSelector(state => state.brands.data);
    const channels = useSelector(state => state.channels.data);
    const account = useSelector(state => state.account.account);
    const dispatch = useDispatch();

    useEffect(() => {
        dispatch(channelsActions.get());
    }, []);

    const onClickAddChannel = () => setCreateModal(prev => ({ ...prev, open: true }));

    const onClickEdit = async channel => {
        setSelectedChannel(channel);
        if (
            channel.type === CHANNEL.WHATSAPP &&
            (channel.status === STATUS.DISCONNECTED || channel.status === STATUS.PENDING)
        ) {
            try {
                const updatedChannel = await channelsService.createOrUpdateChannelWhatsApp(channel);
                setSelectedChannel({ ...updatedChannel, qrCode: null });
                setCreateModal(prev => ({
                    ...prev,
                    open: true,
                    step: 3,
                    type: CHANNEL.WHATSAPP,
                    status: channel.status,
                    qrCode: null,
                }));
            } catch (error) {
                handleApiError(error);
            }
            return;
        }
        setEditModalOpen(true);
    };

    const onClickDelete = channel => {
        setSelectedChannel(channel);
        setDeleteModalOpen(true);
    };

    const onDeleteChannel = async () => {
        try {
            if (selectedChannel?.type === CHANNEL.INSTAGRAM) {
                await channelsService.deleteInstagramChannel(selectedChannel?.id);
            } else {
                await channelsService.deleteChannel(selectedChannel?.id);
            }
            await dispatch(channelsActions.get());
            await dispatch(accountActions.get());
        } catch (error) {
            handleApiError(error, 'alertMessages.deleteFail', { amount: error.count });
        }
        setSelectedChannel({});
        setEditModalOpen(false);
        setDeleteModalOpen(false);
    };

    return (
        <Container>
            <PageHeader
                icon={<ChannelsIcon />}
                title={t('SettingsChannels.headerTitle')}
                subtitle={t('SettingsChannels.headerSubtitle')}
                styles={{ marginBottom: 16 }}
            >
                <Button variant="contained" onClick={onClickAddChannel}>
                    {t('buttons.addChannel')}
                </Button>
            </PageHeader>
            <CardsWrapper>
                {channels.map(channel => (
                    <ChannelItem
                        key={channel.id}
                        channel={channel}
                        companies={companies}
                        brands={brands}
                        onClickDelete={onClickDelete}
                        onClickEdit={onClickEdit}
                    />
                ))}
            </CardsWrapper>
            {createModal.open ? (
                <CreateModal
                    modal={createModal}
                    setModal={setCreateModal}
                    channel={selectedChannel}
                    setChannel={setSelectedChannel}
                    accountLocale={account?.locale}
                />
            ) : null}
            {isEditModalOpen ? (
                <EditModal
                    open={isEditModalOpen}
                    setOpen={setEditModalOpen}
                    companies={companies}
                    selectedChannel={selectedChannel}
                    setSelectedChannel={setSelectedChannel}
                    onClickDelete={onClickDelete}
                    accountLocale={account?.locale}
                />
            ) : null}
            {isDeleteModalOpen && (
                <ModalConfirm
                    isOpen={isDeleteModalOpen}
                    handleClose={() => setDeleteModalOpen(false)}
                    title={t('common.deleteTitle')}
                    description={t('common.deleteDescription')}
                    onPrimaryAction={onDeleteChannel}
                    primaryActionText={t('buttons.delete')}
                    primaryActionType="danger"
                    onSecondaryAction={() => setDeleteModalOpen(false)}
                />
            )}
        </Container>
    );
};

export default Channels;
